import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { superAdminAPI } from '../../services/api';
import { Card, Button, Spinner } from '../UI';
import {
  HiOutlineOfficeBuilding,
  HiOutlineSearch,
  HiOutlineX,
  HiOutlineMail,
  HiOutlineUserAdd,
} from 'react-icons/hi';

/**
 * SuperAdmin → Settings → Company Account
 *
 * TeleValley's own organization on the platform. Demo bookings from the
 * website are added to it as leads, and it gets a daily email + push digest
 * of customer subscriptions that are ending soon, expired or waiting for
 * payment. Backend: services/companyAccountService.js
 */

const Toggle = ({ checked, onChange, label, hint }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      style={{ width: '1rem' }}
    />
    <span>
      <span className="block text-sm font-semibold text-gray-800">{label}</span>
      <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>
    </span>
  </label>
);

const CompanyAccountSettings = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [copying, setCopying] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['sa-company-account'],
    queryFn: async () => (await superAdminAPI.getCompanyAccount()).data?.data,
  });

  useEffect(() => {
    if (data && !form) {
      setForm({
        organization: data.organization,
        demoBookingsToLeads: data.demoBookingsToLeads,
        subscriptionAlerts: data.subscriptionAlerts,
        alertDaysBefore: data.alertDaysBefore,
      });
    }
  }, [data, form]);

  const { data: orgResults = [], isFetching: searching } = useQuery({
    queryKey: ['sa-company-account-orgs', search],
    queryFn: async () => (await superAdminAPI.getOrganizations({ search, limit: 8 })).data?.data || [],
    enabled: search.trim().length >= 2,
  });

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const refresh = (payload) => {
    queryClient.setQueryData(['sa-company-account'], payload);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await superAdminAPI.updateCompanyAccount({
        organizationId: form.organization?._id || null,
        demoBookingsToLeads: form.demoBookingsToLeads,
        subscriptionAlerts: form.subscriptionAlerts,
        alertDaysBefore: Number(form.alertDaysBefore),
      });
      refresh(res.data?.data);
      toast.success('Company account saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const result = (await superAdminAPI.sendCompanyAlerts()).data?.data || {};
      if (result.sent) toast.success(`Digest sent to ${result.recipients.join(', ')}`);
      else toast(result.reason || 'Nothing was sent');
      queryClient.invalidateQueries(['sa-company-account']);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleCopyBookings = async () => {
    setCopying(true);
    try {
      const { copied, total } = (await superAdminAPI.copyDemoBookingsToCompany()).data?.data || {};
      toast.success(`Copied ${copied} of ${total} demo booking${total === 1 ? '' : 's'} as leads`);
      queryClient.invalidateQueries(['sa-company-account']);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to copy demo bookings');
    } finally {
      setCopying(false);
    }
  };

  if (isLoading || !form) {
    return (
      <Card padding="p-6" className="flex items-center justify-center">
        <Spinner />
      </Card>
    );
  }

  // Actions below act on the SAVED account, so they wait until changes are saved
  const savedOrgId = data?.organization?._id || null;
  const dirty =
    (form.organization?._id || null) !== savedOrgId
    || form.demoBookingsToLeads !== data.demoBookingsToLeads
    || form.subscriptionAlerts !== data.subscriptionAlerts
    || Number(form.alertDaysBefore) !== data.alertDaysBefore;
  const preview = data?.preview;

  return (
    <Card padding="p-4 sm:p-6" className="space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
          <HiOutlineOfficeBuilding className="w-5 h-5 text-primary-600" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-gray-900">Company Account</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            TeleValley&apos;s own account on the platform. Website demo bookings are added there as
            leads, and it gets a daily list of customers whose subscription needs a follow-up.
          </p>
        </div>
      </div>

      {/* Organization picker */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Organization</label>
        {form.organization ? (
          <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-primary-200 bg-primary-50">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{form.organization.name}</p>
              <p className="text-xs text-gray-500 truncate">{form.organization.email}</p>
            </div>
            <button
              type="button"
              onClick={() => set('organization', null)}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-white hover:text-gray-700"
              title="Change organization"
            >
              <HiOutlineX className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by organization name or email, e.g. mohitdhadiwal@gmail.com"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {search.trim().length >= 2 && (
              <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden">
                {searching ? (
                  <p className="px-3 py-2 text-sm text-gray-400">Searching…</p>
                ) : orgResults.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-gray-400">No organization found</p>
                ) : (
                  orgResults.map((o) => (
                    <button
                      key={o._id}
                      type="button"
                      onClick={() => { set('organization', { _id: o._id, name: o.name, email: o.email }); setSearch(''); }}
                      className="block w-full text-left px-3 py-2 hover:bg-gray-50"
                    >
                      <span className="block text-sm font-medium text-gray-900">{o.name}</span>
                      <span className="block text-xs text-gray-500">{o.email}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Toggle
          checked={form.demoBookingsToLeads}
          onChange={(v) => set('demoBookingsToLeads', v)}
          label="Add demo bookings as leads"
          hint="Every booking from the website's Book a Demo page becomes a lead in this account, assigned by its lead assignment rules. The account's admins get a push notification."
        />
        <div className="space-y-3">
          <Toggle
            checked={form.subscriptionAlerts}
            onChange={(v) => set('subscriptionAlerts', v)}
            label="Send subscription follow-up alerts"
            hint="Every day at 9:30 AM: an email to this account's email and admins, plus a push notification, listing customers whose subscription is ending soon, expired in the last 7 days, or waiting for payment."
          />
          <div className="pl-7 flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span>Treat as &quot;ending soon&quot; when</span>
            <input
              type="number"
              min="1"
              max="30"
              value={form.alertDaysBefore}
              onChange={(e) => set('alertDaysBefore', e.target.value)}
              disabled={!form.subscriptionAlerts}
              className="px-2 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50"
              style={{ width: '4.5rem' }}
            />
            <span>days or fewer are left</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button onClick={handleSave} loading={saving} disabled={!dirty}>
          Save
        </Button>
      </div>

      {savedOrgId && (
        <div className="border-t border-gray-200 pt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-gray-200 p-4">
            <p className="text-sm font-semibold text-gray-800">Follow-ups right now</p>
            {preview && (
              <p className="text-sm text-gray-600 mt-1">
                {preview.endingSoon} ending soon · {preview.expired} expired · {preview.paymentPending} payment pending
              </p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              Last sent: {data.lastAlertSentAt ? new Date(data.lastAlertSentAt).toLocaleString('en-IN') : 'never'}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              loading={sending}
              disabled={dirty}
              icon={<HiOutlineMail className="w-4 h-4" />}
              onClick={handleSendNow}
            >
              Send digest now
            </Button>
          </div>
          <div className="rounded-lg border border-gray-200 p-4">
            <p className="text-sm font-semibold text-gray-800">Older demo bookings</p>
            <p className="text-sm text-gray-600 mt-1">
              {data.demoBookingsNotCopied
                ? `${data.demoBookingsNotCopied} booking${data.demoBookingsNotCopied === 1 ? ' is' : 's are'} not in this account yet.`
                : 'All demo bookings are already in this account.'}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              loading={copying}
              disabled={dirty || !data.demoBookingsNotCopied}
              icon={<HiOutlineUserAdd className="w-4 h-4" />}
              onClick={handleCopyBookings}
            >
              Copy them as leads
            </Button>
          </div>
          {dirty && <p className="md:col-span-2 text-xs text-amber-600">Save your changes first.</p>}
        </div>
      )}
    </Card>
  );
};

export default CompanyAccountSettings;
